import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import dayjs from 'dayjs';
import deTranslations from '../locales/de.json';
import enTranslations from '../locales/en.json';
import {
  DEFAULT_LANGUAGE,
  isLanguage,
  LANGUAGES,
  LANGUAGE_BY_CODE,
  Language,
} from '../locales/languages';

import { countryTermKey, countryTermParams, TermCountry, toTermCountry } from '../locales/countryTerms';
export type { Language } from '../locales/languages';
export { LANGUAGES, LANGUAGE_BY_CODE } from '../locales/languages';

export const LANGUAGE_STORAGE_KEY = 'preferred-language';

type Bundle = Record<string, unknown>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  /**
   * Translate a dot-path key. Falls back to English, then German, then the
   * key itself, so a language whose bundle only covers part of the app still
   * shows readable text instead of raw keys.
   */
  t: (key: string, params?: Record<string, string | number>) => string;
  /** True while a lazily loaded language bundle is still being fetched. */
  isLoading: boolean;
  /**
   * Land, dessen Fachbegriffe gelten (Pflegegrad statt Pflegestufe, …). Folgt
   * dem Land der Agentur, NICHT der Sprache der Oberfläche — siehe
   * locales/countryTerms.ts.
   */
  termCountry: TermCountry;
  setTermCountry: (country: string | null | undefined) => void;
  availableLanguages: typeof LANGUAGES;
}

// de + en ship in the main bundle (base language and universal fallback).
// Everything else is fetched on demand so 15 locales do not bloat the
// initial download.
const loaders: Partial<Record<Language, () => Promise<{ default: Bundle }>>> = {
  ro: () => import('../locales/ro.json'),
  pl: () => import('../locales/pl.json'),
  sk: () => import('../locales/sk.json'),
  cs: () => import('../locales/cs.json'),
  hu: () => import('../locales/hu.json'),
  sl: () => import('../locales/sl.json'),
  hr: () => import('../locales/hr.json'),
  sr: () => import('../locales/sr.json'),
  bg: () => import('../locales/bg.json'),
  it: () => import('../locales/it.json'),
  fr: () => import('../locales/fr.json'),
  ru: () => import('../locales/ru.json'),
  uk: () => import('../locales/uk.json'),
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

const readStoredLanguage = (): Language => {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLanguage(saved)) return saved;
    // First visit: take a hint from the browser, but only for a language we
    // actually ship (navigator.language looks like "ro-RO" or "de").
    const browser = (navigator.language || '').slice(0, 2).toLowerCase();
    if (isLanguage(browser)) return browser;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return DEFAULT_LANGUAGE;
};

const lookup = (bundle: Bundle | undefined, path: string[]): string | undefined => {
  let value: any = bundle;
  for (const segment of path) {
    if (value && typeof value === 'object' && segment in value) {
      value = value[segment];
    } else {
      return undefined;
    }
  }
  return typeof value === 'string' ? value : undefined;
};

const interpolate = (text: string, params?: Record<string, string | number>): string => {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    name in params ? String(params[name]) : match,
  );
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);
  const [bundles, setBundles] = useState<Partial<Record<Language, Bundle>>>({
    de: deTranslations as Bundle,
    en: enTranslations as Bundle,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [termCountry, setTermCountryState] = useState<TermCountry>('AT');
  const setTermCountry = useCallback((country: string | null | undefined) => {
    setTermCountryState(toTermCountry(country));
  }, []);
  const requested = useRef<Set<Language>>(new Set<Language>(['de', 'en']));

  const ensureBundle = useCallback(async (lang: Language) => {
    const loader = loaders[lang];
    if (!loader || requested.current.has(lang)) return;
    requested.current.add(lang);
    setIsLoading(true);
    try {
      const mod = await loader();
      setBundles(prev => ({ ...prev, [lang]: (mod.default ?? mod) as Bundle }));
    } catch (error) {
      // Keep the app usable: the fallback chain renders German instead.
      console.error(`Failed to load translations for "${lang}"`, error);
      requested.current.delete(lang);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setLanguage = useCallback(
    (lang: Language) => {
      if (!isLanguage(lang)) return;
      setLanguageState(lang);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      } catch {
        /* storage disabled — the choice simply is not remembered */
      }
      void ensureBundle(lang);
    },
    [ensureBundle],
  );

  // Load the persisted / browser-detected language on first render and keep
  // <html lang> in sync for screen readers and the browser's own tooling.
  useEffect(() => {
    void ensureBundle(language);
    document.documentElement.setAttribute('lang', language);
    // Dates were formatted in English ("Su, 20. Sep") because dayjs runs in its
    // default locale. Load the matching locale bundle for weekday/month names.
    void (async () => {
      try {
        await import(
          /* webpackChunkName: "dayjs-locale" */ /* webpackInclude: /(de|en|ro|pl|sk|cs|hu|sl|hr|sr|bg|it|fr|ru|uk)\.js$/ */
          `dayjs/locale/${language}`
        );
        dayjs.locale(language);
      } catch {
        dayjs.locale('de'); // unknown locale: German is the product default
      }
    })();
  }, [language, ensureBundle]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const path = countryTermKey(key, termCountry).split('.');
      const fixed = countryTermParams(key, termCountry);
      const hit =
        lookup(bundles[language], path) ??
        lookup(bundles.en, path) ??
        lookup(bundles.de, path);
      return interpolate(hit ?? key, fixed ? { ...fixed, ...params } : params);
    },
    [bundles, language, termCountry],
  );

  const value = useMemo<LanguageContextType>(
    () => ({
      language,
      setLanguage,
      t,
      isLoading,
      availableLanguages: LANGUAGES,
      termCountry,
      setTermCountry,
    }),
    [language, setLanguage, t, isLoading, termCountry, setTermCountry],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/** antd locale bundle for the active language (date pickers, pagination, ...). */
export const antdLocaleFor = (language: Language) =>
  (LANGUAGE_BY_CODE[language] ?? LANGUAGE_BY_CODE[DEFAULT_LANGUAGE]).antdLocale;
